import { useTranslation, Trans } from 'react-i18next';
import { Alert, Button, Card, CardBody, Label } from 'reactstrap';

export interface OperatorChip { id: number; label: string; }

export interface AutoQtyOperatorPickerProps {
  items: Array<{ seq: number; qty_required: number }>;
  operators: OperatorChip[];
  value: number[]; // selected operator ids
  onChange: (next: number[]) => void;
}

export function AutoQtyOperatorPicker({ items, operators, value, onChange }: AutoQtyOperatorPickerProps) {
  const { t } = useTranslation('picklist');
  function toggle(id: number): void {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  }

  const distribution = simulateAutoQty(items, value);
  const totalQty = items.reduce((sum, it) => sum + it.qty_required, 0);

  return (
    <div>
      <div className="mb-3">
        <Label className="form-label small text-muted mb-2">{t('wizard.auto_qty.operators')}</Label>
        <div className="d-flex gap-2 flex-wrap">
          {operators.map((op) => {
            const isActive = value.includes(op.id);
            return (
              <Button
                key={op.id} type="button"
                color={isActive ? 'primary' : 'light'}
                size="sm"
                onClick={() => toggle(op.id)}>
                <i className={`bi ${isActive ? 'bi-check-circle-fill' : 'bi-circle'} align-bottom me-1`} />
                {op.label}
              </Button>
            );
          })}
        </div>
      </div>

      {value.length >= 2 && (
        <div>
          <h6 className="fs-15 mb-2">{t('wizard.auto_qty.distribution_title')}</h6>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
            {distribution.map((d) => {
              const op = operators.find((o) => o.id === d.operatorId);
              const pct = totalQty > 0 ? Math.round(d.qty / totalQty * 100) : 0;
              return (
                <Card key={d.operatorId} className="border shadow-none mb-0">
                  <CardBody className="p-2">
                    <div className="small text-muted text-truncate">{op?.label ?? d.operatorId}</div>
                    <div className="fs-15 fw-semibold text-primary">
                      {d.qty} <span className="fs-12 fw-normal text-muted">{t('wizard.auto_qty.qty_with_pct_short', { pct })}</span>
                    </div>
                    <div className="small text-muted">
                      <i className="bi bi-list-check align-bottom" /> {t('wizard.auto_qty.tasks_count', { count: d.taskCount })}
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {value.length === 1 && (
        <Alert color="warning" className="small mb-0" role="alert">
          <i className="bi bi-exclamation-triangle align-bottom me-1" />
          <Trans
            ns="picklist"
            i18nKey="wizard.auto_qty.warning_min_2_rich"
            components={{ strong: <strong /> }}
          />
        </Alert>
      )}
    </div>
  );
}

function simulateAutoQty(items: Array<{ seq: number; qty_required: number }>, opIds: number[]):
    Array<{ operatorId: number; qty: number; taskCount: number }> {
  if (opIds.length < 2) return [];
  const totalQty = items.reduce((s, it) => s + it.qty_required, 0);
  if (totalQty === 0) return opIds.map((id) => ({ operatorId: id, qty: 0, taskCount: 0 }));
  const quota = Math.ceil(totalQty / opIds.length);
  const dist = opIds.map((id) => ({ operatorId: id, qty: 0, taskCount: 0 }));
  let cur = 0;
  for (const it of items) {
    let remaining = it.qty_required;
    while (remaining > 0) {
      const room = quota - dist[cur].qty;
      if (room <= 0) {
        cur = (cur + 1) % opIds.length;
        continue;
      }
      const take = Math.min(remaining, room);
      dist[cur].qty += take;
      dist[cur].taskCount += 1;
      remaining -= take;
      if (dist[cur].qty >= quota && remaining > 0) cur = (cur + 1) % opIds.length;
    }
  }
  return dist;
}
